// This class handles the generation and playing of binaural beats
export class BinauralBeatGenerator {
  private audioContext: AudioContext | null = null;
  private leftOscillator: OscillatorNode | null = null;
  private rightOscillator: OscillatorNode | null = null;
  private gainNode: GainNode | null = null;
  private baseFrequency: number = 200; // Base carrier frequency
  private targetFrequency: number = 10; // Target beat frequency
  private isPlaying: boolean = false;
  private volume: number = 0.5;
  private mediaRecorder: MediaRecorder | null = null;
  private recordingChunks: BlobPart[] = [];
  private isRecording: boolean = false;
  private destinationNode: MediaStreamAudioDestinationNode | null = null;
  private audioContextInitialized: boolean = false;
  private leftWaveform: OscillatorType = 'sine';
  private rightWaveform: OscillatorType = 'sine';
  private pendingOperation: boolean = false;

  constructor() {
    const unlockEvents = ['touchstart', 'touchend', 'click', 'mousedown'];
    unlockEvents.forEach(event => {
      document.addEventListener(event, this.initializeAudioContext, { once: true });
    });
  }

  private initializeAudioContext = async () => {
    if (!this.audioContextInitialized) {
      try {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        
        if (this.audioContext.state === 'suspended') {
          const silentBuffer = this.audioContext.createBuffer(1, 1, 22050);
          const source = this.audioContext.createBufferSource();
          source.buffer = silentBuffer;
          source.connect(this.audioContext.destination);
          source.start();
          
          await this.audioContext.resume();
        }
        
        this.audioContextInitialized = true;
        console.log("AudioContext initialized on user interaction");
      } catch (error) {
        console.error("Failed to initialize AudioContext:", error);
      }
    }
  };

  private initialize() {
    if (!this.audioContext) {
      this.initializeAudioContext();
    }
  }

  public setCarrierFrequency(frequency: number) {
    this.baseFrequency = frequency;
    if (this.isPlaying && this.audioContext && this.leftOscillator && this.rightOscillator) {
      this.leftOscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
      this.rightOscillator.frequency.setValueAtTime(frequency + this.targetFrequency, this.audioContext.currentTime);
    }
  }

  public async play(frequency: number, leftWaveform?: string, rightWaveform?: string, carrierFrequency?: number) {
    if (this.pendingOperation) {
      console.log("Operation in progress, waiting...");
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    this.pendingOperation = true;
    
    try {
      await this.initialize();
      if (!this.audioContext) {
        console.error("Cannot play: AudioContext not initialized. Try interacting with the page first.");
        this.pendingOperation = false;
        return;
      }

      if (this.isPlaying) {
        await this.forceStop();
      }

      if (leftWaveform) {
        this.leftWaveform = this.validateWaveform(leftWaveform);
      }
      if (rightWaveform) {
        this.rightWaveform = this.validateWaveform(rightWaveform);
      }
      if (carrierFrequency) {
        this.baseFrequency = carrierFrequency;
      }

      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      if (this.audioContext.state === 'running') {
        this.setupAndPlay(frequency);
      } else {
        console.error("AudioContext is not running. State:", this.audioContext.state);
      }
    } catch (error) {
      console.error("Error starting audio playback:", error);
    } finally {
      this.pendingOperation = false;
    }
  }

  private validateWaveform(waveform: string): OscillatorType {
    const validWaveforms: OscillatorType[] = ['sine', 'square', 'sawtooth', 'triangle'];
    if (validWaveforms.includes(waveform as OscillatorType)) {
      return waveform as OscillatorType;
    }
    return 'sine';
  }

  private setupAndPlay(frequency: number) {
    if (!this.audioContext) return;
    
    this.cleanupAudioNodes();
    
    this.targetFrequency = frequency;

    this.leftOscillator = this.audioContext.createOscillator();
    this.rightOscillator = this.audioContext.createOscillator();
    this.gainNode = this.audioContext.createGain();
    
    this.destinationNode = this.audioContext.createMediaStreamDestination();

    this.leftOscillator.type = this.leftWaveform;
    this.rightOscillator.type = this.rightWaveform;

    const leftPanner = this.audioContext.createStereoPanner();
    const rightPanner = this.audioContext.createStereoPanner();

    leftPanner.pan.setValueAtTime(-1, this.audioContext.currentTime);
    rightPanner.pan.setValueAtTime(1, this.audioContext.currentTime);

    // Convert raw volume (0-100) to normalized volume (0-1)
    const normalizedVolume = this.volume / 100;

    this.leftOscillator.connect(leftPanner);
    this.rightOscillator.connect(rightPanner);
    leftPanner.connect(this.gainNode);
    rightPanner.connect(this.gainNode);
    
    this.gainNode.connect(this.audioContext.destination);
    if (this.destinationNode) {
      this.gainNode.connect(this.destinationNode);
    }

    // Set up timing for smooth start
    const currentTime = this.audioContext.currentTime;
    const startTime = currentTime + 0.1; // Start oscillators slightly in the future
    
    // Set frequencies at start time to ensure stability
    this.leftOscillator.frequency.setValueAtTime(this.baseFrequency, startTime);
    this.rightOscillator.frequency.setValueAtTime(this.baseFrequency + this.targetFrequency, startTime);

    // Set up fade-in starting at the same time as oscillators
    this.gainNode.gain.setValueAtTime(0.005, startTime); // Start at 0.5% volume
    this.gainNode.gain.linearRampToValueAtTime(normalizedVolume, startTime + 1.5); // Ramp up over 1.5 seconds

    // Start oscillators at the same time as fade-in begins
    this.leftOscillator.start(startTime);
    this.rightOscillator.start(startTime);
    
    this.isPlaying = true;
    console.log(`Playing binaural beat at ${frequency}Hz with volume ${normalizedVolume} (raw: ${this.volume}), left waveform: ${this.leftWaveform}, right waveform: ${this.rightWaveform}`);
  }

  private cleanupAudioNodes() {
    try {
      if (this.leftOscillator) {
        this.leftOscillator.stop();
        this.leftOscillator.disconnect();
        this.leftOscillator = null;
      }
      if (this.rightOscillator) {
        this.rightOscillator.stop();
        this.rightOscillator.disconnect();
        this.rightOscillator = null;
      }
      if (this.gainNode) {
        this.gainNode.disconnect();
        this.gainNode = null;
      }
    } catch (error) {
      console.error("Error cleaning up audio nodes:", error);
    }
  }

  private async forceStop() {
    if (!this.isPlaying) return;
    
    if (this.isRecording) {
      await this.stopRecording();
    }
    
    this.cleanupAudioNodes();
    
    // Reset waveform types to default
    this.leftWaveform = 'sine';
    this.rightWaveform = 'sine';
    
    this.isPlaying = false;
    console.log("Forced audio stop completed, waveforms reset to sine");
  }

  public async stop() {
    if (this.pendingOperation) {
      console.log("Operation in progress, waiting...");
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    this.pendingOperation = true;
    
    try {
      if (!this.isPlaying || !this.audioContext || !this.gainNode) {
        this.pendingOperation = false;
        return;
      }

      if (this.isRecording) {
        await this.stopRecording();
      }

      const currentTime = this.audioContext.currentTime;
      const stopTime = currentTime + 0.1;
      
      if (this.gainNode) {
        this.gainNode.gain.cancelScheduledValues(currentTime);
        this.gainNode.gain.linearRampToValueAtTime(0, stopTime);
      }

      setTimeout(() => {
        this.cleanupAudioNodes();
      }, 150);

      this.isPlaying = false;
      console.log("Audio stopping with short fade-out");
    } catch (error) {
      console.error("Error stopping audio:", error);
      await this.forceStop();
    } finally {
      this.pendingOperation = false;
    }
  }

  public setVolume(value: number) {
    // Store the raw volume value (0-100)
    this.volume = value;
    
    console.log(`Setting volume to ${this.volume} (raw value: ${value})`);
    
    if (this.gainNode && this.audioContext) {
      const currentTime = this.audioContext.currentTime;
      
      // Cancel any scheduled volume changes
      this.gainNode.gain.cancelScheduledValues(currentTime);
      
      // Convert to normalized volume (0-1) for the gain node
      const normalizedVolume = this.volume / 100;
      
      // Smoothly transition to the new volume
      this.gainNode.gain.linearRampToValueAtTime(normalizedVolume, currentTime + 0.1);
    }
  }

  public setFrequency(frequency: number) {
    this.targetFrequency = frequency;
    
    if (this.isPlaying && this.audioContext && this.rightOscillator) {
      this.rightOscillator.frequency.setValueAtTime(
        this.baseFrequency + frequency, 
        this.audioContext.currentTime
      );
    }
  }

  public startRecording(): boolean {
    if (!this.isPlaying || !this.destinationNode || this.isRecording) {
      return false;
    }

    this.recordingChunks = [];
    
    try {
      this.mediaRecorder = new MediaRecorder(this.destinationNode.stream);
      
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordingChunks.push(event.data);
        }
      };
      
      this.mediaRecorder.start();
      this.isRecording = true;
      return true;
    } catch (error) {
      console.error("Failed to start recording:", error);
      return false;
    }
  }

  public async stopRecording(): Promise<Blob | null> {
    if (!this.isRecording || !this.mediaRecorder) {
      return null;
    }

    return new Promise<Blob | null>((resolve) => {
      this.mediaRecorder!.onstop = () => {
        const audioBlob = new Blob(this.recordingChunks, { type: 'audio/wav' });
        this.isRecording = false;
        this.recordingChunks = [];
        resolve(audioBlob);
      };
      
      this.mediaRecorder!.stop();
    });
  }

  public isCurrentlyRecording(): boolean {
    return this.isRecording;
  }

  public async generateBinauralBeat(
    frequency: number, 
    duration: number, 
    ambientSounds?: Array<{ id: string; volume: number; path?: string }> 
  ): Promise<Blob> {
    console.log(`Generating binaural beat at ${frequency}Hz with duration ${duration}s and ${ambientSounds?.length || 0} ambient sounds`);
    
    const sampleRate = 44100;
    const offlineCtx = new OfflineAudioContext(2, sampleRate * duration, sampleRate);
    
    const leftOsc = offlineCtx.createOscillator();
    const rightOsc = offlineCtx.createOscillator();
    const gainNode = offlineCtx.createGain();
    
    const baseFreq = 200;
    leftOsc.frequency.setValueAtTime(baseFreq, 0);
    rightOsc.frequency.setValueAtTime(baseFreq + frequency, 0);
    
    const leftPanner = offlineCtx.createStereoPanner();
    const rightPanner = offlineCtx.createStereoPanner();
    
    leftPanner.pan.setValueAtTime(-1, 0);
    rightPanner.pan.setValueAtTime(1, 0);
    
    gainNode.gain.setValueAtTime(0.5, 0);
    
    const fadeTime = 1;
    gainNode.gain.linearRampToValueAtTime(0.5, fadeTime);
    gainNode.gain.linearRampToValueAtTime(0.5, duration - fadeTime);
    gainNode.gain.linearRampToValueAtTime(0, duration);
    
    leftOsc.connect(leftPanner);
    rightOsc.connect(rightPanner);
    leftPanner.connect(gainNode);
    rightPanner.connect(gainNode);
    gainNode.connect(offlineCtx.destination);
    
    leftOsc.start();
    rightOsc.start();
    leftOsc.stop(duration);
    rightOsc.stop(duration);
    
    if (ambientSounds && ambientSounds.length > 0) {
      const activeSounds = ambientSounds.filter(sound => sound.volume > 0 && sound.path);
      console.log(`Adding ${activeSounds.length} active ambient sounds to WAV generation:`, activeSounds);
      
      const audioPromises = activeSounds.map(async (sound) => {
        try {
          if (!sound.path) {
            console.warn(`No path for sound ${sound.id}, skipping`);
            return null;
          }
          
          const soundUrl = sound.path.startsWith('http') 
            ? sound.path 
            : (window.location.origin + (sound.path.startsWith('/') ? sound.path : `/audio/${sound.path}`));
          
          console.log(`Loading ambient sound for WAV from URL: ${soundUrl}`);
          
          const response = await fetch(soundUrl);
          if (!response.ok) {
            console.error(`Failed to load ambient sound: ${soundUrl}, status: ${response.status}`);
            return null;
          }
          
          const arrayBuffer = await response.arrayBuffer();
          
          try {
            const audioBuffer = await offlineCtx.decodeAudioData(arrayBuffer);
            console.log(`Successfully decoded audio for ${sound.id}, duration: ${audioBuffer.duration}s`);
            
            return {
              id: sound.id,
              buffer: audioBuffer,
              volume: sound.volume / 100
            };
          } catch (decodeError) {
            console.error(`Failed to decode audio for ${sound.id}:`, decodeError);
            return null;
          }
        } catch (error) {
          console.error(`Error loading ambient sound ${sound.id}:`, error);
          return null;
        }
      });
      
      try {
        const loadedSounds = (await Promise.all(audioPromises)).filter(Boolean);
        console.log(`Successfully loaded ${loadedSounds.length} ambient sounds for WAV generation`);
        
        for (const sound of loadedSounds) {
          if (!sound) continue;
          
          const source = offlineCtx.createBufferSource();
          source.buffer = sound.buffer;
          
          const soundGain = offlineCtx.createGain();
          soundGain.gain.value = sound.volume * 0.5;
          
          source.connect(soundGain);
          soundGain.connect(offlineCtx.destination);
          
          source.loop = sound.buffer.duration < duration;
          if (source.loop) {
            console.log(`Looping sound ${sound.id} (${sound.buffer.duration}s) to fill ${duration}s`);
          }
          
          source.start();
          source.stop(duration);
        }
      } catch (error) {
        console.error("Error processing ambient sounds for WAV:", error);
      }
    }
    
    console.log("Starting offline rendering of WAV file...");
    const renderedBuffer = await offlineCtx.startRendering();
    console.log("Rendering complete, converting to WAV blob...");
    
    const wavBlob = await this.audioBufferToWaveBlob(renderedBuffer);
    console.log(`WAV blob created, size: ${Math.round(wavBlob.size / 1024)} KB`);
    
    return wavBlob;
  }

  private async audioBufferToWaveBlob(audioBuffer: AudioBuffer): Promise<Blob> {
    const numOfChannels = audioBuffer.numberOfChannels;
    const length = audioBuffer.length;
    const sampleRate = audioBuffer.sampleRate;
    const bitDepth = 16;
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numOfChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = length * blockAlign;
    
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);
    
    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    this.writeString(view, 8, 'WAVE');
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    this.writeString(view, 36, 'data');
    view.setUint32(40, dataSize, true);
    
    const channels = [];
    for (let i = 0; i < numOfChannels; i++) {
      channels.push(audioBuffer.getChannelData(i));
    }
    
    let offset = 44;
    for (let i = 0; i < length; i++) {
      for (let channel = 0; channel < numOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, channels[channel][i]));
        const value = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset, value, true);
        offset += bytesPerSample;
      }
    }
    
    return new Blob([buffer], { type: 'audio/wav' });
  }

  private writeString(view: DataView, offset: number, string: string): void {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  setWaveforms(leftWaveform: string, rightWaveform: string) {
    // Store the new waveform types
    this.leftWaveform = this.validateWaveform(leftWaveform);
    this.rightWaveform = this.validateWaveform(rightWaveform);
    
    // Update existing oscillators if they're running
    if (this.leftOscillator) {
      this.leftOscillator.type = this.leftWaveform;
    }
    if (this.rightOscillator) {
      this.rightOscillator.type = this.rightWaveform;
    }
    
    console.log("Waveforms updated:", {
      left: this.leftWaveform,
      right: this.rightWaveform,
      oscillatorsRunning: !!(this.leftOscillator && this.rightOscillator)
    });
  }
}

export class AmbientSoundPlayer {
  private audioElements: Map<string, HTMLAudioElement> = new Map();
  private volumes: Map<string, number> = new Map();
  private loadingStatus: Map<string, boolean> = new Map();
  private audioUnlocked: boolean = false;
  private initializationPromise: Promise<void> | null = null;
  private pendingOperation: boolean = false;
  private debugMode: boolean = true; // Enable detailed logging

  constructor() {
    const unlockEvents = ['touchstart', 'touchend', 'click', 'mousedown'];
    unlockEvents.forEach(event => {
      document.addEventListener(event, this.unlockAudio, { once: true });
    });
  }

  private unlockAudio = async () => {
    if (this.audioUnlocked) return;

    try {
      const tempAudio = new Audio();
      tempAudio.volume = 0;
      
      // Add an empty source to prevent error
      tempAudio.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
      
      // Play and immediately pause to unlock audio
      await tempAudio.play();
      tempAudio.pause();
      
      // Set audio context state to running if it's suspended
      if (typeof AudioContext !== 'undefined') {
        const audioContext = new AudioContext();
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }
      }
      
      for (const audio of this.audioElements.values()) {
        audio.load();
      }
      
      this.audioUnlocked = true;
      console.log('Audio unlocked successfully');
    } catch (error) {
      console.error('Failed to unlock audio:', error);
    }
  };

  public loadSound(id: string, url: string): void {
    console.log(`Loading sound: ${id} from URL: ${url}`);
    
    const existingAudio = this.audioElements.get(id);
    if (existingAudio && existingAudio.src === url) {
      console.log(`Sound ${id} already loaded with the same URL (${url}), skipping`);
      return;
    }
    
    if (existingAudio) {
      console.log(`Sound ${id} already loaded but with different URL, updating from ${existingAudio.src} to ${url}`);
      existingAudio.src = url;
      existingAudio.load();
      return;
    }

    const audio = new Audio();
    audio.preload = 'auto';
    audio.volume = 0;
    
    // Force mobile devices to play the audio
    audio.setAttribute('playsinline', '');
    audio.setAttribute('webkit-playsinline', '');
    
    this.loadingStatus.set(id, false);
    
    audio.addEventListener('canplaythrough', () => {
      this.loadingStatus.set(id, true);
      console.log(`Sound ${id} loaded successfully and is ready to play from ${audio.src}`);
      const savedVolume = this.volumes.get(id) ?? 0;
      audio.volume = savedVolume / 100;
    });

    audio.addEventListener('error', (e) => {
      console.error(`Error loading sound ${id} from ${url}:`, e);
      this.loadingStatus.set(id, false);
    });

    // Log beginning of loading
    console.log(`Setting audio source for ${id} to ${url}`);
    audio.src = url;
    this.audioElements.set(id, audio);
    
    this.setVolume(id, 0);
    
    // Force immediate loading
    audio.load();
  }

  public async playSound(id: string): Promise<void> {
    console.log(`Attempting to play sound: ${id}`);
    
    if (this.pendingOperation) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    this.pendingOperation = true;
    
    try {
      const audio = this.audioElements.get(id);
      if (!audio) {
        console.warn(`Cannot play sound ${id}: Audio element not found`);
        this.pendingOperation = false;
        return;
      }

      if (!this.audioUnlocked) {
        await this.unlockAudio();
      }

      if (!audio.paused) {
        audio.pause();
      }
      audio.currentTime = 0;
      
      const volume = this.volumes.get(id) || 0;
      if (volume <= 0) {
        console.log(`Not playing ${id} because volume is ${volume}`);
        this.pendingOperation = false;
        return;
      }
      
      const normalizedVolume = volume / 100;
      if (audio.volume !== normalizedVolume) {
        audio.volume = normalizedVolume;
      }
      
      // Ensure the audio loops
      audio.loop = true;
      
      console.log(`Playing ambient sound: ${id} with volume ${audio.volume} (${volume}%)`);
      
      if (!audio.src || audio.src === 'about:blank' || audio.src === window.location.href) {
        console.error(`Invalid source for sound ${id}: ${audio.src}`);
      }
      
      // Add debug information for audio state
      if (this.debugMode) {
        console.log(`Audio ${id} state before play:`, {
          src: audio.src,
          readyState: audio.readyState,
          networkState: audio.networkState,
          error: audio.error,
          volume: audio.volume,
          muted: audio.muted,
          loop: audio.loop
        });
      }
      
      try {
        const playPromise = audio.play();
        
        if (playPromise !== undefined) {
          await playPromise;
          console.log(`Sound ${id} playback started successfully`);
        }
      } catch (playError) {
        console.error(`Failed to play sound ${id}:`, playError);
        
        if (playError.name === 'NotSupportedError' || playError.message.includes('decode')) {
          const originalSrc = audio.src;
          audio.src = originalSrc;
          audio.load();
          
          try {
            await audio.play();
            console.log(`Sound ${id} playback recovered after reload`);
          } catch (retryError) {
            console.error(`Failed to recover sound ${id} playback:`, retryError);
          }
        }
      }
    } catch (error) {
      console.error(`General error playing sound ${id}:`, error);
    } finally {
      this.pendingOperation = false;
    }
  }

  public stopSound(id: string): void {
    const audio = this.audioElements.get(id);
    if (!audio) {
      console.warn(`Cannot stop sound ${id}: Audio element not found`);
      return;
    }
    
    console.log(`Stopping sound: ${id}`);
    audio.pause();
    audio.currentTime = 0;
    
    // Clear the volume for this sound
    this.volumes.delete(id);
  }

  public setVolume(id: string, volume: number): void {
    const audio = this.audioElements.get(id);
    if (!audio) {
      console.warn(`Cannot set volume for sound ${id}: Audio element not found`);
      return;
    }
    
    this.volumes.set(id, volume);
    
    const normalizedVolume = volume / 100;
    audio.volume = normalizedVolume;
    
    console.log(`Setting ${id} volume to ${normalizedVolume} (from ${volume}%)`);
    
    // Only stop the sound if volume is 0
    if (volume <= 0 && !audio.paused) {
      this.stopSound(id);
    }
  }

  public async playAll(): Promise<void> {
    console.log("playAll called - starting playback of all active ambient sounds");
    
    if (this.pendingOperation) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    this.pendingOperation = true;
    
    try {
      // First stop all currently playing sounds without clearing volumes
      this.stopAll(false);
      
      // Wait a short moment to ensure all sounds are stopped
      await new Promise(resolve => setTimeout(resolve, 100));
      
      let activeSoundsCount = 0;
      const playPromises: Promise<void>[] = [];
      
      const activeSounds: string[] = [];
      for (const [id, volume] of this.volumes.entries()) {
        if (volume > 0) {
          activeSounds.push(id);
        }
      }
      
      console.log(`Found ${activeSounds.length} active sounds to play:`, activeSounds);
      
      for (const id of activeSounds) {
        const audio = this.audioElements.get(id);
        if (audio) {
          // Reset the audio element before playing
          audio.currentTime = 0;
          console.log(`Playing ambient sound: ${id} at volume ${this.volumes.get(id)}%`);
          playPromises.push(this.playSound(id));
          activeSoundsCount++;
        }
      }
      
      await Promise.all(playPromises);
      
      console.log(`Total active sounds played: ${activeSoundsCount}`);
      
      if (activeSoundsCount === 0) {
        console.warn("No active sounds found with volume > 0");
      }
    } catch (error) {
      console.error("Error in playAll:", error);
    } finally {
      this.pendingOperation = false;
    }
  }

  public stopAll(clearVolumes: boolean = true): void {
    console.log("Stopping all ambient sounds");
    
    try {
      for (const [id, audio] of this.audioElements.entries()) {
        if (!audio.paused) {
          console.log(`Stopping sound: ${id}`);
          audio.pause();
          audio.currentTime = 0;
        }
      }
      
      // Only clear volumes if explicitly requested
      if (clearVolumes) {
        this.volumes.clear();
      }
    } catch (error) {
      console.error("Error stopping all ambient sounds:", error);
    }
  }

  public seekSound(id: string, timeInSeconds: number): void {
    const audio = this.audioElements.get(id);
    if (!audio) {
      console.warn(`Cannot seek sound ${id}: Audio element not found`);
      return;
    }

    // Calculate the actual time to seek to based on the audio duration
    const actualTime = timeInSeconds % audio.duration;
    audio.currentTime = actualTime;

    // If the audio should loop, ensure it's set to loop
    audio.loop = true;

    console.log(`Seeking sound ${id} to ${actualTime}s (${timeInSeconds}s requested)`);
  }

  public seekAll(timeInSeconds: number): void {
    console.log(`Seeking all ambient sounds to ${timeInSeconds}s`);
    
    for (const [id, audio] of this.audioElements.entries()) {
      if (!audio.paused) {
        this.seekSound(id, timeInSeconds);
      }
    }
  }
}
